/**
 * X (Twitter) Integration Settings
 * ---------------------------------
 * Production-ready tenant settings page for the MemoraAI X social-listening
 * feature. Talks to:
 *   • GET  /api/twitter/config      → load this tenant's config
 *   • PUT  /api/twitter/config      → save full config
 *   • PUT  /api/twitter/trial-mode  → quick trial toggle
 *   • POST /api/twitter/test-tweet  → dry-run preview the AI pipeline
 *   • GET  /api/twitter/status      → global env flags + last-run summary
 *
 * Design notes:
 *   • Plain Tailwind + Shadcn/UI primitives (no extra deps).
 *   • React Hook Form + Zod for validation (already in package.json).
 *   • Sonner toasts for non-blocking feedback.
 *   • Mobile-first; cards stack at <md.
 *   • Strict accessibility: every input has a Label, every button has
 *     a data-testid for the testing agent.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  Twitter, Power, Beaker, ShieldCheck, AlertTriangle, X as XIcon,
  Loader2, Send, RefreshCcw, Hash, MapPin, Users, Gauge, CheckCircle2,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import BusinessAdminLayout from "../layouts/BusinessAdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ──────────────────────────── Validation ──────────────────────────────
const ConfigSchema = z.object({
  x_active: z.boolean(),
  x_keywords: z.array(z.string().min(1).max(80))
    .max(25, "Max 25 keywords"),
  x_locations: z.array(z.string()),
  x_match_categories: z.array(z.string()),
  x_min_followers: z.coerce.number().int().min(0).max(1_000_000),
  x_daily_outreach_cap: z.coerce.number().int().min(0).max(500),
  x_outreach_template_name: z.string().optional().nullable(),
  x_outreach_template_lang: z.string().optional().nullable(),
});

// ──────────────────────────── TagInput ────────────────────────────────
/**
 * Lightweight tag-list input. Adds on Enter / comma; removes via × badge.
 * Kept inline (not a separate file) since it has no other consumer.
 */
function TagInput({ value = [], onChange, placeholder, max = 25, testid }) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const add = (raw) => {
    const t = (raw || "").trim().replace(/,$/, "");
    if (!t) return;
    if (value.length >= max) {
      toast.error(`Max ${max} entries`);
      return;
    }
    if (value.includes(t)) return;
    onChange([...value, t]);
    setDraft("");
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div
      className="flex flex-wrap gap-1.5 px-2 py-1.5 border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-sky-400 bg-white min-h-[42px]"
      onClick={() => inputRef.current?.focus()}
      data-testid={testid}
    >
      {value.map((t, i) => (
        <Badge key={`${t}-${i}`} variant="secondary" className="gap-1 px-2 py-1 text-xs">
          {t}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); remove(i); }}
            className="hover:text-red-600"
            aria-label={`Remove ${t}`}
            data-testid={`${testid}-remove-${i}`}
          >
            <XIcon className="w-3 h-3" />
          </button>
        </Badge>
      ))}
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add(draft);
          } else if (e.key === "Backspace" && !draft && value.length) {
            remove(value.length - 1);
          }
        }}
        onBlur={() => draft && add(draft)}
        placeholder={value.length ? "" : placeholder}
        className="flex-1 min-w-[120px] outline-none text-sm bg-transparent px-1"
      />
    </div>
  );
}

// ───────────────────────── Status badge ───────────────────────────────
function StatusBadge({ config, status, remainingSeconds }) {
  if (!status?.enabled) {
    return (
      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
        <Power className="w-3 h-3 mr-1" /> Global listener disabled
      </Badge>
    );
  }
  if (!config?.x_active) {
    return (
      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
        <Power className="w-3 h-3 mr-1" /> Tenant paused
      </Badge>
    );
  }
  if (config?.x_trial_mode) {
    const remain = formatRemaining(remainingSeconds);
    return (
      <Badge className="bg-violet-100 text-violet-800 border-0">
        <Beaker className="w-3 h-3 mr-1" />
        Trial active {remain ? `· expires in ${remain}` : ""}
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-100 text-emerald-800 border-0">
      <CheckCircle2 className="w-3 h-3 mr-1" /> Active · sending real outreach
    </Badge>
  );
}

function formatRemaining(secs) {
  if (!secs && secs !== 0) return "";
  if (secs <= 0) return "0h";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

// ───────────────────────── Main page ──────────────────────────────────
export default function TwitterSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingTrial, setTogglingTrial] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testText, setTestText] = useState(
    "Looking for 3 BHK in Hyderabad near IT corridor under 1 Cr. WhatsApp 9876543210"
  );
  const [testResult, setTestResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [trialRemaining, setTrialRemaining] = useState(null);
  const [config, setConfig] = useState(null);
  const [bearerDraft, setBearerDraft] = useState("");

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  }), []);

  const {
    register, handleSubmit, control, reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(ConfigSchema),
    defaultValues: {
      x_active: false,
      x_keywords: [],
      x_locations: [],
      x_match_categories: [],
      x_min_followers: 5,
      x_daily_outreach_cap: 25,
      x_outreach_template_name: "",
      x_outreach_template_lang: "en",
    },
  });

  // ── Load initial config + global status in parallel ────────────────
  const load = async () => {
    setLoading(true);
    try {
      const [cfgR, stR] = await Promise.all([
        fetch(`${API}/twitter/config`, { headers }),
        fetch(`${API}/twitter/status`, { headers }),
      ]);
      const cfg = await cfgR.json();
      const st = await stR.json();
      setConfig(cfg);
      setStatus(st);
      reset({
        x_active: !!cfg.x_active,
        x_keywords: cfg.x_keywords || [],
        x_locations: cfg.x_locations || [],
        x_match_categories: cfg.x_match_categories || [],
        x_min_followers: cfg.x_min_followers ?? 5,
        x_daily_outreach_cap: cfg.x_daily_outreach_cap ?? 25,
        x_outreach_template_name: cfg.x_outreach_template_name || "",
        x_outreach_template_lang: cfg.x_outreach_template_lang || "en",
      });
      // Compute initial remaining time from `x_trial_started_at`
      if (cfg.x_trial_mode && cfg.x_trial_started_at) {
        const trialDays = st.trial_mode_days || 3;
        const startMs = new Date(cfg.x_trial_started_at).getTime();
        const endMs = startMs + trialDays * 86400 * 1000;
        setTrialRemaining(Math.max(0, Math.floor((endMs - Date.now()) / 1000)));
      } else {
        setTrialRemaining(null);
      }
    } catch (e) {
      toast.error("Failed to load X settings");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ── Tick the trial countdown every minute (cheap, no re-fetch) ────
  useEffect(() => {
    if (trialRemaining == null) return;
    const t = setInterval(() => {
      setTrialRemaining((s) => (s == null ? null : Math.max(0, s - 60)));
    }, 60_000);
    return () => clearInterval(t);
  }, [trialRemaining != null]);

  // ── Save full config ───────────────────────────────────────────────
  const onSubmit = async (data) => {
    setSaving(true);
    try {
      // Preserve current trial state — we don't expose it on this form,
      // the dedicated toggle handles it. The PUT endpoint requires it
      // in the payload though, so we pass through what we already have.
      const payload = { ...data, x_trial_mode: !!config?.x_trial_mode };
      // Per-tenant X bearer token — only sent when the operator actually
      // typed a new one (the field stays empty after save for security).
      if (bearerDraft.trim()) {
        payload.x_bearer_token = bearerDraft.trim();
      }
      const r = await fetch(`${API}/twitter/config`, {
        method: "PUT", headers, body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      const fresh = await r.json();
      setConfig(fresh);
      setBearerDraft("");
      toast.success("X settings saved");
    } catch (e) {
      toast.error(e.message || "Save failed");
    }
    setSaving(false);
  };

  // ── Trial mode toggle (independent endpoint, instant) ─────────────
  const toggleTrial = async (enabled) => {
    setTogglingTrial(true);
    try {
      const r = await fetch(`${API}/twitter/trial-mode`, {
        method: "PUT", headers, body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error("Toggle failed");
      const j = await r.json();
      setConfig(j);
      setTrialRemaining(j.trial_remaining_seconds ?? null);
      toast.success(
        enabled
          ? `Trial mode ON — outreach will be drafted but NOT sent for ${j.trial_days_total} days`
          : "Trial mode OFF — real outreach will resume next cycle"
      );
    } catch (e) {
      toast.error(e.message);
    }
    setTogglingTrial(false);
  };

  // ── Dry-run a sample tweet through the AI ─────────────────────────
  const runTest = async () => {
    if (!testText.trim()) return toast.error("Paste a tweet first");
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/twitter/test-tweet`, {
        method: "POST", headers, body: JSON.stringify({
          text: testText, author_name: "Test User",
          author_username: "testuser", follower_count: 100,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      setTestResult(await r.json());
    } catch (e) {
      toast.error(e.message);
    }
    setTesting(false);
  };

  // ──────────────────────────── Render ────────────────────────────────
  if (loading) {
    return (
      <BusinessAdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-sky-600" />
        </div>
      </BusinessAdminLayout>
    );
  }

  return (
    <BusinessAdminLayout>
      <div className="space-y-6 max-w-5xl" data-testid="twitter-settings-page">
        {/* ────────────── Header ────────────── */}
        <header className="flex flex-wrap items-start gap-3 justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 inline-flex items-center gap-2">
              <Twitter className="w-7 h-7 text-sky-500" />
              X (Twitter) Integration
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Listen to public X posts and route real leads to your WhatsApp.
            </p>
          </div>
          <StatusBadge config={config} status={status} remainingSeconds={trialRemaining} />
        </header>

        {!status?.enabled && (
          <Card className="border-amber-200 bg-amber-50/60">
            <CardContent className="py-3 text-sm text-amber-900 inline-flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                X listener is currently disabled platform-wide. Your settings
                will be saved, but no live tweets will be polled until the
                SaaS admin enables it from <code className="bg-amber-100 px-1 rounded">/saas-admin/twitter</code>.
              </span>
            </CardContent>
          </Card>
        )}
        {status?.enabled && !config?.has_x_bearer_token && !status?.bearer_token_set && (
          <Card className="border-amber-200 bg-amber-50/60">
            <CardContent className="py-3 text-sm text-amber-900 inline-flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                You haven&apos;t pasted your X bearer token yet. Tweets won&apos;t
                be polled for this tenant until you do — see the
                &ldquo;X API credentials&rdquo; card below.
              </span>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* ────────────── Activation & Trial ────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Power className="w-4 h-4 text-sky-600" /> Activation
              </CardTitle>
              <CardDescription>Master switches for this tenant.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <Controller
                control={control}
                name="x_active"
                render={({ field }) => (
                  <ToggleRow
                    label="Enable X Listening"
                    helper="When OFF, no tweets are polled for this tenant."
                    checked={field.value}
                    onChange={field.onChange}
                    testid="toggle-x-active"
                  />
                )}
              />
              <ToggleRow
                label="Trial Mode (safe mode)"
                helper="When ON, leads are detected & drafted but NO real WhatsApp or X reply is sent. Auto-expires after the global trial window."
                checked={!!config?.x_trial_mode}
                onChange={toggleTrial}
                loading={togglingTrial}
                testid="toggle-x-trial"
              />
              {config?.x_trial_mode && (
                <div className="text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Trial expires in <b>{formatRemaining(trialRemaining) || "—"}</b>
                  {" · "}
                  Real outreach resumes automatically after expiry.
                </div>
              )}
            </CardContent>
          </Card>

          {/* ────────────── X API credentials (per-tenant) ────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-sky-600" /> X API credentials
              </CardTitle>
              <CardDescription>
                Paste your own X (Twitter) developer{" "}
                <b>App-only Bearer Token</b>. Each tenant uses their own X
                app — no rate-limit contention. Token is stored encrypted
                and never displayed back.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                {config?.has_x_bearer_token ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-0">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Bearer token saved
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 border-0">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    No bearer token yet
                  </Badge>
                )}
                {config?.has_x_bearer_token && (
                  <span className="text-[11px] text-gray-500">
                    Leave the field empty to keep the current one.
                  </span>
                )}
              </div>
              <Input
                type="password"
                placeholder={
                  config?.has_x_bearer_token
                    ? "•••••••••••••••••••••••••••••••• (leave empty to keep)"
                    : "AAAAAAAAAAAAAAAAAAAAA..."
                }
                value={bearerDraft}
                onChange={(e) => setBearerDraft(e.target.value)}
                className="font-mono text-xs"
                data-testid="x-bearer-input"
                autoComplete="new-password"
              />
              <p className="text-[11px] text-gray-500">
                Get it from{" "}
                <a
                  href="https://developer.x.com/en/portal/projects-and-apps"
                  target="_blank" rel="noreferrer"
                  className="text-sky-600 underline"
                >
                  developer.x.com → Projects & Apps → Keys and Tokens
                </a>{" "}
                · <b>Bearer Token</b>. Requires the <b>Basic tier ($200/mo)</b> or
                higher to use Recent Search v2.
              </p>
            </CardContent>
          </Card>

          {/* ────────────── Keywords / Locations / Categories ────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Hash className="w-4 h-4 text-sky-600" /> Targeting
              </CardTitle>
              <CardDescription>
                What the listener should look for. Press Enter or comma to add.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <FormBlock
                label="Keywords (max 25)"
                hint='Quote multi-word phrases automatically. Examples: "3 bhk hyderabad", "plot for sale".'
                error={errors.x_keywords?.message}
              >
                <Controller
                  control={control}
                  name="x_keywords"
                  render={({ field }) => (
                    <TagInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder='e.g. "3 bhk hyderabad", brochure...'
                      max={25}
                      testid="x-keywords"
                    />
                  )}
                />
              </FormBlock>

              <FormBlock
                label="Locations"
                hint="Use 2-letter ISO country codes (e.g. IN) to filter by place_country. City names act as keywords."
              >
                <Controller
                  control={control}
                  name="x_locations"
                  render={({ field }) => (
                    <TagInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Hyderabad, Shamirpet, IN..."
                      testid="x-locations"
                    />
                  )}
                />
              </FormBlock>

              <FormBlock
                label="Match Categories"
                hint="Free-form tags used by the AI to gate relevance (e.g. Villa Plots, Independent Houses, Commercial)."
              >
                <Controller
                  control={control}
                  name="x_match_categories"
                  render={({ field }) => (
                    <TagInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Villa Plots, Independent Houses..."
                      testid="x-match-categories"
                    />
                  )}
                />
              </FormBlock>
            </CardContent>
          </Card>

          {/* ────────────── Safety thresholds ────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Gauge className="w-4 h-4 text-sky-600" /> Safety thresholds
              </CardTitle>
              <CardDescription>Per-tenant rate limits.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FormBlock
                label="Min Followers"
                hint="Skip posts from accounts with fewer followers than this."
                icon={<Users className="w-4 h-4" />}
                error={errors.x_min_followers?.message}
              >
                <Input
                  type="number" min={0}
                  {...register("x_min_followers", { valueAsNumber: true })}
                  data-testid="x-min-followers"
                />
              </FormBlock>
              <FormBlock
                label="Daily Outreach Cap"
                hint="Max real outreach sends per day for this tenant (0..500)."
                icon={<MapPin className="w-4 h-4" />}
                error={errors.x_daily_outreach_cap?.message}
              >
                <Input
                  type="number" min={0} max={500}
                  {...register("x_daily_outreach_cap", { valueAsNumber: true })}
                  data-testid="x-daily-cap"
                />
              </FormBlock>
              <FormBlock label="WABA Template Name (optional)" hint="Default: leadintroductiontemplate">
                <Input
                  placeholder="leadintroductiontemplate"
                  {...register("x_outreach_template_name")}
                  data-testid="x-template-name"
                />
              </FormBlock>
              <FormBlock label="Template Language" hint='Defaults to "en"'>
                <Input
                  placeholder="en"
                  {...register("x_outreach_template_lang")}
                  data-testid="x-template-lang"
                />
              </FormBlock>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-3 sticky bottom-3 z-10 bg-white/80 backdrop-blur px-4 py-3 -mx-4 sm:mx-0 sm:px-0 sm:bg-transparent sm:backdrop-blur-0 sm:static border-t sm:border-0">
            <Button
              type="submit" disabled={saving || !isDirty}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="x-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Save settings
            </Button>
            <Button
              type="button" variant="outline" onClick={load}
              data-testid="x-refresh-btn"
            >
              <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            {isDirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
          </div>
        </form>

        {/* ────────────── Dry-run test ────────────── */}
        <Card className="border-violet-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Beaker className="w-4 h-4 text-violet-600" /> Dry-run preview
            </CardTitle>
            <CardDescription>
              Run a sample tweet through the AI to see how the listener would
              classify it. Does <b>not</b> send anything, doesn&apos;t consume X API quota.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Sample tweet</Label>
              <Textarea
                rows={3}
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder="Paste any tweet text here..."
                className="resize-y"
                data-testid="x-test-textarea"
              />
            </div>
            <Button
              onClick={runTest} disabled={testing}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="x-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Beaker className="w-4 h-4 mr-1" />}
              Test tweet
            </Button>
            {testResult && <TestResult result={testResult} />}
          </CardContent>
        </Card>

        {/* ────────────── Status footer ────────────── */}
        <Card className="bg-slate-50/40">
          <CardContent className="py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <KV label="Listener" value={status?.enabled ? "ON" : "OFF"} good={!!status?.enabled} />
            <KV label="Bearer set" value={status?.bearer_token_set ? "Yes" : "No"} good={!!status?.bearer_token_set} />
            <KV label="Interval" value={`${status?.interval_minutes || "—"} min`} />
            <KV label="Global daily cap" value={status?.global_daily_cap ?? "—"} />
            {status?.last_run?.ran_at && (
              <KV
                label="Last sweep"
                value={`${new Date(status.last_run.ran_at).toLocaleString()} · ${status.last_run.leads_created || 0} leads`}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </BusinessAdminLayout>
  );
}

// ────────────── tiny presentational helpers ──────────────
function ToggleRow({ label, helper, checked, onChange, testid, loading }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <Label className="text-sm font-medium text-gray-900">{label}</Label>
        {helper && <p className="text-xs text-gray-500 mt-0.5 max-w-md">{helper}</p>}
      </div>
      <div className="flex items-center gap-2">
        {loading && <Loader2 className="w-4 h-4 animate-spin text-sky-600" />}
        <Switch
          checked={!!checked}
          onCheckedChange={onChange}
          disabled={loading}
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function FormBlock({ label, hint, error, icon, children }) {
  return (
    <div>
      <Label className="text-xs text-gray-600 flex items-center gap-1">
        {icon}{label}
      </Label>
      {hint && <p className="text-[11px] text-gray-400 mt-0.5 mb-1.5">{hint}</p>}
      <div className="mt-1">{children}</div>
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function KV({ label, value, good }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-sm font-semibold ${good ? "text-emerald-700" : "text-gray-800"}`}>
        {String(value)}
      </p>
    </div>
  );
}

function TestResult({ result }) {
  const ai = result.ai_response || {};
  const isLead = !!ai.is_lead;
  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white" data-testid="x-test-result">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={isLead ? "bg-emerald-100 text-emerald-800 border-0" : "bg-slate-100 text-slate-700 border-0"}>
          {isLead ? "Is lead" : "Not a lead"}
        </Badge>
        <Badge variant="outline">confidence {Math.round((ai.confidence || 0) * 100)}%</Badge>
        {ai.intent && <Badge variant="outline">intent: {ai.intent}</Badge>}
        {ai.language && <Badge variant="outline">lang: {ai.language}</Badge>}
        {result.would_extract_phone && (
          <Badge variant="outline">📞 {result.would_extract_phone}</Badge>
        )}
        {result.contains_optout_keyword && (
          <Badge className="bg-red-100 text-red-700 border-0">opt-out</Badge>
        )}
      </div>
      {ai.reason && (
        <p className="text-sm text-gray-700"><b>Reason:</b> {ai.reason}</p>
      )}
      {result.preview?.whatsapp_message && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-emerald-700 mb-1">WhatsApp draft</p>
          <p className="text-sm bg-emerald-50/60 border border-emerald-100 rounded-lg p-3 whitespace-pre-wrap">
            {result.preview.whatsapp_message}
          </p>
        </div>
      )}
      {result.preview?.public_reply && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-sky-700 mb-1">Public X reply draft</p>
          <p className="text-sm bg-sky-50/60 border border-sky-100 rounded-lg p-3 whitespace-pre-wrap">
            {result.preview.public_reply}
          </p>
        </div>
      )}
    </div>
  );
}
